import { AnimatingLogo } from '@eazydraft/ui';
import { useQuery } from '@tanstack/react-query';
import { TriangleAlert } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate } from 'react-router';

import { getAuthMe } from '@/api/generated/auth/auth';
import microsoftLogo from '@/assets/icons/microsoftLogo.png';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { AnimatedFullScreenLoader } from '@/components/ui/animated-full-screen-loader';
import { Button } from '@/components/ui/button';
import { hasDeviceSession } from '@/config/auth/deviceToken';
import { queryKeys } from '@/config/query/queryKeys';
import { useConnectMailbox } from '@/features/auth/hooks/useConnectMailbox';
import { useSignOut } from '@/features/auth/hooks/useSignOut';
import { ROUTES } from '@/utils/routeUtils';

interface ScreenCopy {
  button: string;
  subtitle: string;
  title: string;
}

const screenCopy = (t: (key: string) => string, isReconnect: boolean): ScreenCopy =>
  isReconnect
    ? {
        button: t('connectMailbox.reconnectButton'),
        subtitle: t('connectMailbox.reconnectSubtitle'),
        title: t('connectMailbox.reconnectTitle'),
      }
    : {
        button: t('connectMailbox.button'),
        subtitle: t('connectMailbox.subtitle'),
        title: t('connectMailbox.title'),
      };

interface ConnectMailboxViewProps {
  connectLabel: string;
  connectPending: boolean;
  copy: ScreenCopy;
  errorMessage: string | null;
  onConnect: () => void;
  onSignOut: () => void;
  signOutLabel: string;
  signOutPending: boolean;
}

// Pure presentational JSX; cognitive score is nesting depth, not logic.
// fallow-ignore-next-line complexity
const ConnectMailboxView = ({
  connectLabel,
  connectPending,
  copy,
  errorMessage,
  onConnect,
  onSignOut,
  signOutLabel,
  signOutPending,
}: ConnectMailboxViewProps) => (
  <div className="h-viewport flex flex-col items-center justify-center bg-white px-2">
    <AnimatingLogo alt="Lucio AI Logo" className="mb-4 h-16 w-auto" src="/assets/lucioSymbol.svg" />

    <div className="w-full max-w-sm">
      <div className="mb-8 text-center">
        <h1 className="text-primary mb-1 text-lg font-semibold">{copy.title}</h1>
        <p className="text-xs text-gray-600">{copy.subtitle}</p>
      </div>

      <div className="space-y-3">
        <Button
          className="flex w-full items-center justify-center gap-2 border-gray-300 bg-white py-6 font-medium text-gray-700 hover:bg-gray-50"
          disabled={connectPending}
          loading={connectPending}
          onClick={onConnect}
          variant="outline"
        >
          <img alt="Microsoft" className="h-5 w-5" src={microsoftLogo} />
          {connectLabel}
        </Button>

        <Button
          className="w-full text-xs text-gray-500"
          disabled={signOutPending}
          onClick={onSignOut}
          variant="ghost"
        >
          {signOutLabel}
        </Button>
      </div>

      {errorMessage && (
        <Alert className="mt-6 w-full" data-testid="error-alert" variant="destructive">
          <TriangleAlert className="h-4 w-4" />
          <AlertDescription>{errorMessage}</AlertDescription>
        </Alert>
      )}
    </div>
  </div>
);

/**
 * Post-sign-in gate: the user has a Clerk session but no connected mailbox
 * (first run) or a revoked Graph credential (reconnect). Microsoft sign-in
 * (Office dialog; see msalConnectDialog.ts) acquires an identity token that
 * the backend exchanges via OBO for the stored Graph credential that powers
 * background sync and automations.
 */
export const ConnectMailbox = () => {
  const { t } = useTranslation();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const signedIn = hasDeviceSession();
  const { data: me, isLoading } = useQuery({
    enabled: signedIn,
    queryFn: () => getAuthMe(),
    queryKey: queryKeys.auth.session(),
  });

  const connectMutation = useConnectMailbox(setErrorMessage);
  const signOutMutation = useSignOut();

  if (!signedIn) {
    return <Navigate replace to={ROUTES.LOGIN} />;
  }

  if (isLoading) {
    return <AnimatedFullScreenLoader show={true} />;
  }

  const mailboxConnected = me?.mailboxConnected === true;
  const graphTokenActive = me?.graphTokenActive === true;

  // Already fully connected — nothing to do here.
  if (mailboxConnected && graphTokenActive) {
    return <Navigate replace to={ROUTES.HOME} />;
  }

  const copy = screenCopy(t, mailboxConnected);

  return (
    <ConnectMailboxView
      connectLabel={connectMutation.isPending ? t('connectMailbox.connecting') : copy.button}
      connectPending={connectMutation.isPending}
      copy={copy}
      errorMessage={errorMessage}
      onConnect={() => {
        setErrorMessage(null);
        connectMutation.mutate();
      }}
      onSignOut={() => signOutMutation.mutate()}
      signOutLabel={t('connectMailbox.signOut')}
      signOutPending={signOutMutation.isPending}
    />
  );
};
